import { Trans } from 'react-i18next';
import React, { useMemo, ChangeEvent, useCallback } from 'react';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import Select from '@mui/material/Select';
import Switch from '@mui/material/Switch';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import HelpIcon from '@mui/icons-material/Help';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import CloseIcon from '@mui/icons-material/Close';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import Autocomplete from '@mui/material/Autocomplete';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import FormControlLabel from '@mui/material/FormControlLabel';
import DialogContentText from '@mui/material/DialogContentText';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';

import useLocales from 'src/hooks/useLocales';

import ColorPicker from 'src/components/color-picker/ColorPicker';

import { useSelector } from '../../../redux/store';
import { setIsLoading } from '../../../redux/slices/calendar';
import { getEventTypesList } from '../../../services/calendarService';
import { ModuleTypes } from '../../../@types/module/enums/moduleTypes';
import ModuleCheck from '../../../components/access-checks/ModuleCheck';
import { Collaborator, ElementProps } from '../../../@types/forms/types';
import { useElementSync } from './element-settings/hooks/useElementSync';
// Import hooks
import { useElementState } from './element-settings/hooks/useElementState';
import { FormElementType } from '../../../@types/forms/enums/FormElementType';
import { CustomTooltip } from './element-settings/components/StyledComponents';
import { useElementHandlers } from './element-settings/hooks/useElementHandlers';
import ElementImageUpload from './element-settings/components/ElementImageUpload';
import ElementOptionsManager from './element-settings/components/ElementOptionsManager';
// Import types and constants
import { AddressBookType } from './element-settings/constants/elementSettingsConstants';
import { ElementSettingsActionType } from './element-settings/types/elementSettingsTypes';
import ElementUserDataSettings from './element-settings/components/ElementUserDataSettings';
// Import sub-components
import ElementTextStyleSettings from './element-settings/components/ElementTextStyleSettings';
import ElementDateRangeSettings from './element-settings/components/ElementDateRangeSettings';
import ElementHyperlinkSettings from './element-settings/components/ElementHyperlinkSettings';
import ElementValidationSettings from './element-settings/components/ElementValidationSettings';
import ElementChildElementsManager from './element-settings/components/ElementChildElementsManager';
import {
  getBase64FromFile,
  getImageDimensions,
} from './element-settings/utils/elementSettingsUtils';

type ElementSettingsDialogProps = {
  open: boolean;
  element: ElementProps | null;
  elements: ElementProps[];
  collaborators: Collaborator[];
  setElements: (elements: ElementProps[]) => void;
  handleClose: (isDialogOpen: boolean) => void;
  handleDelete: () => void;
  handleChange: (changedElement: ElementProps) => void;
};

const ElementSettingsDialog: React.FC<ElementSettingsDialogProps> = ({
  open,
  element,
  elements,
  collaborators,
  setElements,
  handleClose,
  handleDelete,
  handleChange,
}) => {
  const { t, currentLang } = useLocales();
  const { eventTypesList } = useSelector((state) => state.calendar);

  // Initialize state using custom hook
  const { state, dispatch } = useElementState(t);

  // Get memoized handlers
  const handlers = useElementHandlers(dispatch, state, t);

  // Sync state with parent and external data
  useElementSync(element, state, dispatch, handleChange, elements, eventTypesList, open);

  // Additional handlers specific to this component
  const handleFileUpload = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      const { files } = event.target;

      if (files?.length) {
        try {
          const base64 = await getBase64FromFile(files[0]);
          dispatch({ type: ElementSettingsActionType.SET_UPLOADED_FILE, payload: base64 });

          // Get image dimensions for Image element type
          if (base64 && state.selectedElement?.type === FormElementType.Image) {
            const dimensions = await getImageDimensions(base64);
            dispatch({
              type: ElementSettingsActionType.UPDATE_ELEMENT_FIELD,
              payload: { imageDimensions: dimensions },
            });
          }
        } catch (error) {
          console.error('Failed to upload file:', error);
        }
      }
    },
    [dispatch, state.selectedElement?.type]
  );

  const handleAddressBookTypeChange = useCallback(
    (newType: AddressBookType) => {
      dispatch({
        type: ElementSettingsActionType.SET_ADDRESS_BOOK_TYPE,
        payload: {
          isForAll: newType === AddressBookType.All,
          isForExternal: newType === AddressBookType.External,
          isForInternal: newType === AddressBookType.Internal,
        },
      });
    },
    [dispatch]
  );

  const handleAddChildElement = useCallback(() => {
    if (state.selectedChildElement) {
      dispatch({
        type: ElementSettingsActionType.ADD_CHILD_ELEMENT,
        payload: state.selectedChildElement,
      });

      // Mark element as child in the elements list
      const newElements = [...elements];
      newElements.forEach((el) => {
        if (el.id === state.selectedChildElement!.id) {
          el.isChild = true;
        }
      });
      setElements(newElements);
    }
  }, [dispatch, state.selectedChildElement, elements, setElements]);

  const handleDeleteChildElement = useCallback(
    (index: number) => {
      const childToDelete = state.childElements[index];

      // Mark element as non-child in the elements list
      const newElements = [...elements];
      newElements.forEach((el) => {
        if (el.id === childToDelete.id) {
          el.isChild = false;
        }
      });
      setElements(newElements);

      dispatch({ type: ElementSettingsActionType.DELETE_CHILD_ELEMENT, payload: index });
    },
    [dispatch, state.childElements, elements, setElements]
  );

  const clearStateAndClose = useCallback(() => {
    dispatch({ type: ElementSettingsActionType.RESET_STATE, payload: t('shared.countries.lt') });
    handleClose(false);
  }, [dispatch, handleClose, t]);

  const addressBookType = useMemo(() => {
    if (state.isForInternal) return AddressBookType.Internal;
    if (state.isForExternal) return AddressBookType.External;
    return AddressBookType.All;
  }, [state.isForInternal, state.isForExternal]);

  // Fetch event types list on mount if needed
  React.useEffect(() => {
    if (eventTypesList.length === 0) {
      try {
        getEventTypesList({ offset: 0, limit: 9999, searchQuery: '' });
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    }
  }, [eventTypesList.length]);

  if (!element) {
    return null;
  }

  const showTextStyleSettings =
    state.selectedElement?.type !== FormElementType.Image &&
    state.selectedElement?.type !== FormElementType.Signature;

  const showChildElementsManager =
    (state.selectedElement?.type === FormElementType.InputField ||
      state.selectedElement?.type === FormElementType.Date ||
      state.selectedElement?.type === FormElementType.DateRange) &&
    !state.selectedElement?.isChild;

  const showRequiredSwitch =
    element.type === FormElementType.InputField ||
    element.type === FormElementType.Dropdown ||
    element.type === FormElementType.Checkbox ||
    element.type === FormElementType.RadioButton ||
    element.type === FormElementType.Date ||
    element.type === FormElementType.DateRange ||
    element.type === FormElementType.CustomList ||
    element.type === FormElementType.Signature ||
    element.type === FormElementType.AddressBook;

  return (
    <Dialog open={open} fullWidth maxWidth="xs" scroll="paper">
      <DialogTitle
        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}
      >
        {`${t('forms.editElement')}: ${state.selectedElement?.label || ''}`}

        <IconButton size="small" onClick={clearStateAndClose} id="kt7iavzx">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <DialogContentText tabIndex={-1}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
            {/* Collaborator */}
            <ModuleCheck module={ModuleTypes.CollabForms}>
              {state.selectedElement?.collaboratorId !== undefined && (
                <Autocomplete
                  fullWidth
                  size="small"
                  options={collaborators}
                  value={
                    collaborators.find(
                      (c) => c.collaboratorId === state.selectedElement?.collaboratorId
                    ) || null
                  }
                  onChange={(_, newValue) => {
                    handlers.handleCollaboratorChange(newValue?.collaboratorId);
                  }}
                  getOptionLabel={(option) =>
                    option.order === 0
                      ? `${t('forms.owner')}`
                      : `${t('forms.collaborator', { number: option.order })}`
                  }
                  renderInput={(params) => (
                    <TextField {...params} label={t('forms.collaborators')} variant="outlined" />
                  )}
                  id="element-collaborator-select"
                />
              )}
            </ModuleCheck>

            {/* Integration ID */}
            <TextField
              fullWidth
              label={t('forms.elements.settings.integrationId')}
              variant="outlined"
              size="small"
              value={state.selectedElement?.doclogixCoreAttributeShortName || ''}
              onChange={(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                handlers.handleDoclogixFieldIdChange(event.target.value);
              }}
              id="17ta291m"
              slotProps={{
                inputLabel: {
                  shrink: Boolean(state.selectedElement?.doclogixCoreAttributeShortName),
                }
              }}
            />

            {/* Element Label */}
            <TextField
              fullWidth
              label={t('forms.elements.settings.title')}
              variant="outlined"
              size="small"
              value={state.selectedElement?.label || ''}
              onChange={(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                handlers.handleLabelChange(event.target.value);
              }}
              id="km7jl08b"
              slotProps={{
                inputLabel: {
                  shrink: Boolean(state.selectedElement?.label),
                }
              }}
            />

            {/* Text Style Settings */}
            {showTextStyleSettings && state.selectedElement && (
              <ElementTextStyleSettings
                font={state.selectedElement.font}
                fontSize={state.selectedElement.fontSize}
                textAlignment={state.selectedElement.textAlignment}
                elementTextStyle={state.elementTextStyle}
                elementTextDecoration={state.elementTextDecoration}
                textRotation={state.textRotation}
                selectedColor={state.selectedColor}
                isOpenTextRotation={state.isOpenTextRotation}
                isOpenColor={state.isOpenColor}
                onFontChange={handlers.handleFontChange}
                onFontSizeChange={handlers.handleFontSizeChange}
                onAlignmentChange={handlers.handleAlignmentChange}
                onTextStyleChange={handlers.handleTextStyleChange}
                onTextDecorationChange={handlers.handleTextDecorationChange}
                onSliderChange={handlers.handleSliderChange}
                onTextRotationInputChange={handlers.handleTextRotationInputChange}
                onTextRotationBlur={handlers.handleTextRotationBlur}
                onTextColorChange={handlers.handleTextColorChange}
                onToggleTextRotation={() =>
                  dispatch({ type: ElementSettingsActionType.TOGGLE_TEXT_ROTATION })
                }
                onToggleColor={() => dispatch({ type: ElementSettingsActionType.TOGGLE_COLOR })}
              />
            )}

            {/* Dropdown Options */}
            {state.selectedElement?.type === FormElementType.Dropdown && (
              <ElementOptionsManager
                options={state.options}
                type="simple"
                onAdd={handlers.handleAddOption}
                onDelete={handlers.handleDeleteOption}
                onChange={handlers.handleOptionChange}
                onFocus={handlers.handleOptionFocus}
                onBlur={handlers.handleOptionBlur}
              />
            )}

            {/* Checkbox Options */}
            {state.selectedElement?.type === FormElementType.Checkbox && (
              <ElementOptionsManager
                options={state.formGroupOptions}
                type="formGroup"
                onAdd={handlers.handleAddFormGroupOption}
                onDelete={handlers.handleDeleteFormGroupOption}
                onChange={handlers.handleFormGroupOptionChange}
                onFocus={handlers.handleFormGroupFocus}
                onBlur={handlers.handleFormGroupBlur}
              />
            )}

            {/* RadioButton Options */}
            {state.selectedElement?.type === FormElementType.RadioButton && (
              <ElementOptionsManager
                options={state.formGroupOptions}
                type="formGroup"
                onAdd={handlers.handleAddFormGroupOption}
                onDelete={handlers.handleDeleteFormGroupOption}
                onChange={handlers.handleFormGroupOptionChange}
                onFocus={handlers.handleFormGroupFocus}
                onBlur={handlers.handleFormGroupBlur}
              />
            )}

            {/* Custom List */}
            {state.selectedElement?.type === FormElementType.CustomList && (
              <Autocomplete
                id="IbZvuL"
                value={state.selectedList}
                onChange={(event: any, newList) => {
                  dispatch({ type: ElementSettingsActionType.SET_SELECTED_LIST, payload: newList });
                }}
                options={state.customLists}
                getOptionLabel={(option) => option.name}
                fullWidth
                size="small"
                renderOption={(props, option) => {
                  const { ...optionProps } = props;
                  return (
                    <Box id={`id-IbZvuL-${option.id}`} component="li" {...optionProps}>
                      {option.name} {option.id}
                    </Box>
                  );
                }}
                renderInput={(params) => (
                  <TextField {...params} label={t('forms.elements.settings.lists')} />
                )}
              />
            )}

            {/* Image Upload */}
            {state.selectedElement?.type === FormElementType.Image && (
              <ElementImageUpload
                uploadedFile={state.uploadedFile}
                elementLabel={state.selectedElement.label}
                onFileUpload={handleFileUpload}
              />
            )}

            {/* Signature Color Picker */}
            {state.selectedElement?.type === FormElementType.Signature && (
              <Box sx={{ mt: 2 }}>
                <ColorPicker
                  color={state.selectedColor || '#221271'}
                  setColor={handlers.handleTextColorChange}
                  spacing={2}
                />
              </Box>
            )}

            {/* Static Text */}
            {state.selectedElement?.type === FormElementType.StaticText && (
              <TextField
                label="Multiline"
                value={state.staticText || ''}
                multiline
                maxRows={4}
                onChange={handlers.handleStaticTextChange}
                id="fFGH5ui"
                slotProps={{
                  inputLabel: {
                    shrink: !!state.staticText,
                  }
                }}
              />
            )}

            {/* Input Field Validation */}
            {state.selectedElement?.type === FormElementType.InputField && (
              <ElementValidationSettings
                validation={state.validation}
                numberOfCharacters={state.numberOfCharacters}
                onValidationChange={(validation) =>
                  dispatch({ type: ElementSettingsActionType.SET_VALIDATION, payload: validation })
                }
                onNumberOfCharactersChange={handlers.handleNumberOfCharactersChange}
              />
            )}

            {/* Date Range Settings */}
            {state.selectedElement?.type === FormElementType.DateRange && (
              <ElementDateRangeSettings
                daysOfCountry={state.daysOfCountry}
                dateRangeTemplate={state.dateRangeTemplate}
                openedTabIndex={state.openedTabIndex}
                eventTypesList={eventTypesList}
                calendarEventType={state.calendarEventType}
                onDaysOfCountryChange={(country) =>
                  dispatch({
                    type: ElementSettingsActionType.SET_DAYS_OF_COUNTRY,
                    payload: country,
                  })
                }
                onDateRangeTemplateChange={(template) =>
                  dispatch({
                    type: ElementSettingsActionType.SET_DATE_RANGE_TEMPLATE,
                    payload: template,
                  })
                }
                onTabIndexChange={(index) =>
                  dispatch({ type: ElementSettingsActionType.SET_TAB_INDEX, payload: index })
                }
                onCalendarEventTypeChange={(eventType) =>
                  dispatch({
                    type: ElementSettingsActionType.SET_CALENDAR_EVENT_TYPE,
                    payload: eventType,
                  })
                }
                currentLang={currentLang}
              />
            )}

            {/* Hyperlink Settings */}
            {state.selectedElement?.type === FormElementType.Hyperlink && (
              <ElementHyperlinkSettings
                hyperlink={state.hyperlink}
                onHyperlinkChange={(hyperlink) =>
                  dispatch({ type: ElementSettingsActionType.SET_HYPERLINK, payload: hyperlink })
                }
              />
            )}

            {/* User Data Settings */}
            {state.selectedElement?.type === FormElementType.User && (
              <ElementUserDataSettings
                userInfo={state.userInfo}
                onUserInfoChange={handlers.handleUserInfoChange}
              />
            )}

            {/* Registration Number Prefix */}
            {state.selectedElement?.type === FormElementType.RegistrationNumber && (
              <TextField
                label={`${t('forms.elements.settings.uniqueIdentifier')}`}
                value={state.prefix || ''}
                onChange={handlers.handleUniqueIdentifierChange}
                id="YlyX1j"
              />
            )}

            {/* Child Elements Manager */}
            {showChildElementsManager && (
              <ElementChildElementsManager
                childElements={state.childElements}
                sameTypeElementsList={state.sameTypeElementsList}
                selectedChildElement={state.selectedChildElement}
                onChildInputChange={(child) =>
                  dispatch({
                    type: ElementSettingsActionType.SET_SELECTED_CHILD_ELEMENT,
                    payload: child,
                  })
                }
                onAddChildElement={handleAddChildElement}
                onDeleteChildElement={handleDeleteChildElement}
              />
            )}

            {/* Date - Today Date Switch */}
            {state.selectedElement?.type === FormElementType.Date && (
              <FormControlLabel
                control={
                  <Switch
                    checked={state.todayDate}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                      dispatch({
                        type: ElementSettingsActionType.SET_TODAY_DATE,
                        payload: event.target.checked,
                      })
                    }
                    id="m4ecak6n"
                  />
                }
                label={t('forms.todayDate')}
              />
            )}

            {/* Date/DateRange - Disable Past Dates Switch */}
            {(state.selectedElement?.type === FormElementType.Date ||
              state.selectedElement?.type === FormElementType.DateRange) && (
              <FormControlLabel
                control={
                  <Switch
                    disabled={state.todayDate}
                    checked={state.todayDate ? false : state.isPastDisabled}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                      dispatch({
                        type: ElementSettingsActionType.SET_PAST_DISABLED,
                        payload: event.target.checked,
                      })
                    }
                    id="m4ecak6n"
                  />
                }
                label={t('forms.disablePastDates')}
              />
            )}

            {/* Address Book Type Selection */}
            {element.type === FormElementType.AddressBook && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                }}
              >
                <FormControl fullWidth>
                  <InputLabel id="options-dropdown-label">{t('forms.selectOption')}</InputLabel>
                  <Select
                    size="small"
                    labelId="options-dropdown-label"
                    value={addressBookType}
                    onChange={(e) => handleAddressBookTypeChange(e.target.value as AddressBookType)}
                    label={t('forms.selectOption')}
                    IconComponent={ExpandMoreRoundedIcon}
                    id="JdnR00"
                  >
                    <MenuItem id="JdnR01" value={AddressBookType.All}>
                      {t('forms.all')}
                    </MenuItem>
                    <MenuItem id="JdnR02" value={AddressBookType.Internal}>
                      {t('forms.internal')}
                    </MenuItem>
                    <MenuItem id="JdnR03" value={AddressBookType.External}>
                      {t('forms.external')}
                    </MenuItem>
                  </Select>
                </FormControl>

                <CustomTooltip
                  title={
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        p: 1,
                      }}
                    >
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <Trans
                          i18nKey="forms.allDescritpion"
                          values={{ all: t('forms.all') }}
                          components={{ bold: <strong /> }}
                        />
                      </Typography>

                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <Trans
                          i18nKey="forms.internalDescription"
                          values={{ internal: t('forms.internal') }}
                          components={{ bold: <strong /> }}
                        />
                      </Typography>

                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <Trans
                          i18nKey="forms.externalDescription"
                          values={{ external: t('forms.external') }}
                          components={{ bold: <strong /> }}
                        />
                      </Typography>
                    </Box>
                  }
                  arrow
                >
                  <HelpIcon />
                </CustomTooltip>
              </Box>
            )}

            {/* Required Switch */}
            {showRequiredSwitch && (
              <FormControlLabel
                control={
                  <Switch
                    disabled={state.todayDate}
                    checked={state.todayDate ? false : state.isElementRequired}
                    color="primary"
                    onClick={() =>
                      dispatch({
                        type: ElementSettingsActionType.SET_ELEMENT_REQUIRED,
                        payload: !state.isElementRequired,
                      })
                    }
                    id="VrdTaF"
                  />
                }
                label={`${t('forms.required')}`}
              />
            )}
          </Box>
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'left', gap: 3 }}>
            <Button
              type="submit"
              variant="outlined"
              color="error"
              onClick={() => {
                clearStateAndClose();
                handleDelete();
              }}
              id="6pKJkV"
            >
              {t('shared.buttons.delete')}
            </Button>

            <Button type="submit" variant="contained" onClick={clearStateAndClose} id="yd0sown5">
              {t('forms.buttons.save')}
            </Button>
          </Box>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default ElementSettingsDialog;
